/**
 * Utility functions for selecting which events to display on portfolio charts.
 * Events are selected based on:
 * 1. Time-distributed buckets to ensure even spread across chart
 * 2. Days with the largest absolute price change within each bucket
 * 3. Maximum of 10 events total
 */

export interface DailyChange {
  dateStr: string;
  absChange: number;
}

/**
 * Valid time ranges for chart event selection.
 */
export type ChartTimeRange = '1w' | '1m' | '3m' | 'ytd' | '1y' | '5y';

/**
 * Minimum day gap between chart events for each time range.
 * Shorter time ranges allow events closer together.
 */
export const MIN_DAY_GAP: Record<ChartTimeRange, number> = {
  '1w': 1,
  '1m': 3,
  '3m': 5,
  ytd: 5,
  '1y': 5,
  '5y': 10,
};

/**
 * Maximum number of events to show on the chart.
 */
export const MAX_EVENTS = 10;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Select the best dates to show events using time-distributed buckets.
 *
 * This ensures events are spread across the entire chart period rather than
 * clustering in high-volatility periods. The algorithm:
 * 1. Divides the time range into MAX_EVENTS equal buckets
 * 2. From each bucket, selects the event with the highest absolute change
 * 3. Sorts candidates by absChange so higher-change events win gap conflicts
 * 4. Respects minimum day gap between events
 *
 * Time complexity: O(n) for bucketing + O(k log k) for sorting candidates
 *
 * @param dailyChanges - Array of dates with their absolute change values
 * @param timeRange - The current time range (e.g., '1w', '1m', 'ytd')
 * @returns Set of date strings (YYYY-MM-DD) that should show events
 */
export function selectEventDates(dailyChanges: DailyChange[], timeRange: ChartTimeRange): Set<string> {
  if (dailyChanges.length === 0) return new Set();

  const gap = MIN_DAY_GAP[timeRange];
  const gapMs = gap * MS_PER_DAY;

  // Get date range
  const dates = dailyChanges.map((d) => new Date(d.dateStr).getTime());
  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const totalRange = maxDate - minDate;

  // If range is too small for bucketing, use greedy selection with gap constraint
  if (totalRange < gapMs * 2) {
    const sorted = [...dailyChanges].sort((a, b) => b.absChange - a.absChange);
    const selectedDates = new Set<string>();
    const selectedMs: number[] = [];

    for (const { dateStr } of sorted) {
      if (selectedDates.size >= MAX_EVENTS) break;

      const dateMs = new Date(dateStr).getTime();
      let tooClose = false;
      for (const selMs of selectedMs) {
        if (Math.abs(dateMs - selMs) < gapMs) {
          tooClose = true;
          break;
        }
      }

      if (!tooClose) {
        selectedDates.add(dateStr);
        selectedMs.push(dateMs);
      }
    }

    return selectedDates;
  }

  // Divide time range into buckets (one per max event)
  const bucketSize = totalRange / MAX_EVENTS;

  // Group events into buckets
  const buckets: DailyChange[][] = Array.from({ length: MAX_EVENTS }, () => []);

  for (const change of dailyChanges) {
    const dateMs = new Date(change.dateStr).getTime();
    let bucketIndex = Math.floor((dateMs - minDate) / bucketSize);
    // Handle edge case where dateMs === maxDate
    if (bucketIndex >= MAX_EVENTS) bucketIndex = MAX_EVENTS - 1;
    buckets[bucketIndex].push(change);
  }

  // Select best event from each bucket (highest absolute change)
  const candidates: DailyChange[] = [];
  for (const bucket of buckets) {
    if (bucket.length > 0) {
      // Find max by absChange in O(n) instead of sorting
      const best = bucket.reduce((a, b) => (a.absChange > b.absChange ? a : b));
      candidates.push(best);
    }
  }

  // Sort candidates by absChange descending so higher-change events win gap conflicts
  candidates.sort((a, b) => b.absChange - a.absChange);

  // Apply minimum gap constraint to final selection
  const selectedDates = new Set<string>();
  const selectedMs: number[] = [];

  for (const { dateStr } of candidates) {
    const dateMs = new Date(dateStr).getTime();

    // Check if too close to already selected dates
    let tooClose = false;
    for (const selMs of selectedMs) {
      if (Math.abs(dateMs - selMs) < gapMs) {
        tooClose = true;
        break;
      }
    }

    if (!tooClose) {
      selectedDates.add(dateStr);
      selectedMs.push(dateMs);
    }
  }

  return selectedDates;
}
